/**
 * React Hook for Configuration Management
 * Provides hot-reloadable configuration with React state management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import configurationService, { CustomerConfiguration } from '../services/configurationService';

interface UseConfigurationReturn {
  config: CustomerConfiguration | null;
  loading: boolean;
  error: string | null;
  usingDefaults: boolean;
  updateConfiguration: (updates: Record<string, any>) => Promise<void>;
  testConfiguration: (testConfig: Record<string, any>, sampleDocument?: any) => Promise<any>;
  refreshConfiguration: () => Promise<void>;
}

export const useConfiguration = (): UseConfigurationReturn => {
  const [config, setConfig] = useState<CustomerConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingDefaults, setUsingDefaults] = useState(false);
  const fallbackFired = useRef(false);
  const isAuthenticated = useIsAuthenticated();

  // Load initial configuration — wait until authenticated so the Bearer token is available
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('🔄 Loading configuration...');
        const configuration = await configurationService.getConfiguration();
        console.log('✅ Configuration loaded:', configuration);
        setConfig(configuration);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load configuration');
        console.error('❌ Error loading configuration:', err);
        if (!fallbackFired.current) {
          fallbackFired.current = true;
          setUsingDefaults(true);
        }
      } finally {
        setLoading(false);
      }
    };

    loadConfig();

    // Subscribe to configuration changes
    const unsubscribe = configurationService.onConfigurationChange((newConfig) => {
      setConfig(newConfig);
      setError(null);
    });

    return unsubscribe;
  }, [isAuthenticated]);

  // Update configuration
  const updateConfiguration = useCallback(async (updates: Record<string, any>) => {
    try {
      setError(null);
      await configurationService.updateConfiguration(updates);
      // Configuration will be updated via the subscription
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update configuration';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Test configuration
  const testConfiguration = useCallback(async (testConfig: Record<string, any>, sampleDocument?: any) => {
    try {
      setError(null);
      return await configurationService.testConfiguration(testConfig, sampleDocument);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to test configuration';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Refresh configuration
  const refreshConfiguration = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await configurationService.refreshConfiguration();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh configuration');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    config,
    loading,
    error,
    usingDefaults,
    updateConfiguration,
    testConfiguration,
    refreshConfiguration
  };
};

/**
 * Hook for specific configuration sections
 */
export const useConfigurationSection = <T>(
  selector: (config: CustomerConfiguration) => T
): {
  data: T | null;
  loading: boolean;
  error: string | null;
} => {
  const { config, loading, error } = useConfiguration();

  const data = config ? selector(config) : null;

  return { data, loading, error };
};

// Convenience hooks for common configuration sections
export const useUIConfiguration = () => 
  useConfigurationSection(config => config.ui);

export const useAIConfiguration = () => 
  useConfigurationSection(config => config.ai);

export const useProcessingConfiguration = () => 
  useConfigurationSection(config => config.processing);

export const useIntegrationConfiguration = () => 
  useConfigurationSection(config => config.integration);

export default useConfiguration;